/**
 * Guitar Pedal Board Component
 * Virtual pedal chain with distortion, overdrive, fuzz, wah, and modulation effects
 */

import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Plus,
  X,
  GripVertical,
  Power,
  Zap,
  Flame,
  Waves,
  Wind,
  Music,
  Settings,
  ChevronDown,
  Volume2,
  RotateCcw,
} from 'lucide-react';

// Pedal Types with their characteristics
const PEDAL_TYPES = {
  distortion: {
    name: 'Distortion',
    category: 'drive',
    icon: Zap,
    color: 'from-orange-500 to-red-500',
    description: 'Heavy clipping distortion',
    params: {
      drive: { label: 'Drive', min: 0, max: 100, default: 60 },
      tone: { label: 'Tone', min: 0, max: 100, default: 50 },
      level: { label: 'Level', min: 0, max: 100, default: 70 },
    },
  },
  overdrive: {
    name: 'Overdrive',
    category: 'drive',
    icon: Flame,
    color: 'from-amber-500 to-orange-500',
    description: 'Tube-style warm overdrive',
    params: {
      drive: { label: 'Drive', min: 0, max: 100, default: 45 },
      tone: { label: 'Tone', min: 0, max: 100, default: 55 },
      level: { label: 'Level', min: 0, max: 100, default: 65 },
    },
  },
  fuzz: {
    name: 'Fuzz',
    category: 'drive',
    icon: Waves,
    color: 'from-violet-500 to-purple-600',
    description: 'Vintage fuzz face style',
    params: {
      fuzz: { label: 'Fuzz', min: 0, max: 100, default: 70 },
      tone: { label: 'Tone', min: 0, max: 100, default: 45 },
      volume: { label: 'Volume', min: 0, max: 100, default: 60 },
    },
  },
  wah: {
    name: 'Wah',
    category: 'filter',
    icon: Wind,
    color: 'from-emerald-500 to-teal-500',
    description: 'Classic wah pedal',
    params: {
      position: { label: 'Position', min: 0, max: 100, default: 50 },
      range: { label: 'Range', min: 0, max: 100, default: 70 },
      resonance: { label: 'Q', min: 0, max: 100, default: 60 },
    },
  },
  chorus: {
    name: 'Chorus',
    category: 'modulation',
    icon: Music,
    color: 'from-cyan-500 to-blue-500',
    description: 'Lush stereo chorus',
    params: {
      rate: { label: 'Rate', min: 0, max: 100, default: 40 },
      depth: { label: 'Depth', min: 0, max: 100, default: 50 },
      mix: { label: 'Mix', min: 0, max: 100, default: 50 },
    },
  },
  phaser: {
    name: 'Phaser',
    category: 'modulation',
    icon: Waves,
    color: 'from-pink-500 to-rose-500',
    description: 'Sweeping phase shifter',
    params: {
      rate: { label: 'Rate', min: 0, max: 100, default: 35 },
      depth: { label: 'Depth', min: 0, max: 100, default: 60 },
      feedback: { label: 'Feedback', min: 0, max: 100, default: 50 },
    },
  },
  flanger: {
    name: 'Flanger',
    category: 'modulation',
    icon: Wind,
    color: 'from-indigo-500 to-violet-500',
    description: 'Jet-like flanging',
    params: {
      rate: { label: 'Rate', min: 0, max: 100, default: 30 },
      depth: { label: 'Depth', min: 0, max: 100, default: 55 },
      feedback: { label: 'Feedback', min: 0, max: 100, default: 65 },
    },
  },
  tremolo: {
    name: 'Tremolo',
    category: 'modulation',
    icon: Volume2,
    color: 'from-rose-500 to-red-500',
    description: 'Rhythmic volume modulation',
    params: {
      rate: { label: 'Rate', min: 0, max: 100, default: 50 },
      depth: { label: 'Depth', min: 0, max: 100, default: 60 },
      wave: { label: 'Wave', min: 0, max: 100, default: 50 },
    },
  },
  compressor: {
    name: 'Compressor',
    category: 'dynamics',
    icon: Settings,
    color: 'from-slate-500 to-zinc-600',
    description: 'Optical compressor',
    params: {
      threshold: { label: 'Sustain', min: 0, max: 100, default: 55 },
      attack: { label: 'Attack', min: 0, max: 100, default: 30 },
      level: { label: 'Level', min: 0, max: 100, default: 70 },
    },
  },
  noisegate: {
    name: 'Noise Gate',
    category: 'dynamics',
    icon: X,
    color: 'from-zinc-600 to-zinc-700',
    description: 'Remove unwanted noise',
    params: {
      threshold: { label: 'Threshold', min: 0, max: 100, default: 40 },
      decay: { label: 'Decay', min: 0, max: 100, default: 50 },
    },
  },
  // NEW PEDAL EFFECTS
  octaver: {
    name: 'Octaver',
    category: 'pitch',
    icon: Music,
    color: 'from-purple-500 to-fuchsia-500',
    description: 'Sub-octave and upper octave blend',
    params: {
      octDown: { label: 'Oct -1', min: 0, max: 100, default: 50 },
      octUp: { label: 'Oct +1', min: 0, max: 100, default: 30 },
      dry: { label: 'Dry', min: 0, max: 100, default: 70 },
    },
  },
  pitchShifter: {
    name: 'Pitch Shifter',
    category: 'pitch',
    icon: Waves,
    color: 'from-teal-500 to-emerald-500',
    description: 'Intelligent pitch shifting',
    params: {
      shift: { label: 'Shift', min: -12, max: 12, default: 0 },
      fine: { label: 'Fine', min: -50, max: 50, default: 0 },
      mix: { label: 'Mix', min: 0, max: 100, default: 50 },
    },
  },
  delay: {
    name: 'Delay',
    category: 'time',
    icon: Waves,
    color: 'from-blue-500 to-cyan-500',
    description: 'Digital delay with tap tempo',
    params: {
      time: { label: 'Time', min: 0, max: 100, default: 40 },
      feedback: { label: 'Feedback', min: 0, max: 100, default: 35 },
      mix: { label: 'Mix', min: 0, max: 100, default: 30 },
    },
  },
  reverb: {
    name: 'Reverb',
    category: 'time',
    icon: Wind,
    color: 'from-sky-500 to-blue-600',
    description: 'Lush hall reverb',
    params: {
      decay: { label: 'Decay', min: 0, max: 100, default: 50 },
      predelay: { label: 'Pre-Delay', min: 0, max: 100, default: 20 },
      mix: { label: 'Mix', min: 0, max: 100, default: 25 },
    },
  },
  looper: {
    name: 'Looper',
    category: 'utility',
    icon: RotateCcw,
    color: 'from-green-500 to-emerald-600',
    description: '60-second loop recording',
    params: {
      level: { label: 'Level', min: 0, max: 100, default: 80 },
      feedback: { label: 'Feedback', min: 0, max: 100, default: 100 },
      speed: { label: 'Speed', min: 50, max: 200, default: 100 },
    },
  },
  booster: {
    name: 'Clean Boost',
    category: 'drive',
    icon: Zap,
    color: 'from-yellow-400 to-amber-500',
    description: 'Transparent clean boost',
    params: {
      boost: { label: 'Boost', min: 0, max: 100, default: 50 },
      tone: { label: 'Tone', min: 0, max: 100, default: 50 },
    },
  },
  eqPedal: {
    name: 'EQ Pedal',
    category: 'utility',
    icon: Settings,
    color: 'from-gray-500 to-slate-600',
    description: '6-band graphic EQ',
    params: {
      low: { label: 'Low', min: -12, max: 12, default: 0 },
      mid: { label: 'Mid', min: -12, max: 12, default: 0 },
      high: { label: 'High', min: -12, max: 12, default: 0 },
    },
  },
  autoWah: {
    name: 'Auto Wah',
    category: 'filter',
    icon: Wind,
    color: 'from-lime-500 to-green-500',
    description: 'Envelope-controlled filter',
    params: {
      sensitivity: { label: 'Sens', min: 0, max: 100, default: 60 },
      range: { label: 'Range', min: 0, max: 100, default: 70 },
      resonance: { label: 'Q', min: 0, max: 100, default: 55 },
    },
  },
  vibrato: {
    name: 'Vibrato',
    category: 'modulation',
    icon: Waves,
    color: 'from-orange-400 to-rose-500',
    description: 'Classic pitch vibrato',
    params: {
      rate: { label: 'Rate', min: 0, max: 100, default: 45 },
      depth: { label: 'Depth', min: 0, max: 100, default: 40 },
    },
  },
  rotary: {
    name: 'Rotary',
    category: 'modulation',
    icon: RotateCcw,
    color: 'from-amber-600 to-orange-600',
    description: 'Leslie speaker simulation',
    params: {
      speed: { label: 'Speed', min: 0, max: 100, default: 50 },
      drive: { label: 'Drive', min: 0, max: 100, default: 30 },
      mix: { label: 'Mix', min: 0, max: 100, default: 60 },
    },
  },
};

// Individual Pedal Component
const Pedal = ({ pedal, onToggle, onParamChange, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pedalType = PEDAL_TYPES[pedal.type];
  const Icon = pedalType?.icon || Zap;
  
  if (!pedalType) return null;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`relative rounded-xl border overflow-hidden transition-all ${
        pedal.enabled 
          ? `bg-gradient-to-br ${pedalType.color} border-white/20` 
          : 'bg-zinc-800/80 border-zinc-700'
      }`}
      style={{ minWidth: '140px' }}
    >
      {/* Drag Handle */}
      <div className="absolute top-2 left-2 cursor-grab active:cursor-grabbing text-white/50 hover:text-white/80">
        <GripVertical className="w-4 h-4" />
      </div>
      
      {/* Remove Button */}
      <button
        onClick={() => onRemove(pedal.id)}
        className="absolute top-2 right-2 text-white/50 hover:text-white/80"
      >
        <X className="w-4 h-4" />
      </button>
      
      {/* Pedal Face */}
      <div className="pt-8 pb-4 px-4">
        {/* LED */}
        <div className="flex justify-center mb-3">
          <div className={`w-3 h-3 rounded-full ${pedal.enabled ? 'bg-red-500 shadow-lg shadow-red-500/50' : 'bg-zinc-600'}`} />
        </div>
        
        {/* Name */}
        <h4 className={`text-center font-bold text-sm mb-2 ${pedal.enabled ? 'text-white' : 'text-zinc-400'}`}>
          {pedalType.name}
        </h4>
        
        {/* Mini knobs preview */}
        <div className="flex justify-center gap-2 mb-3">
          {Object.entries(pedalType.params).slice(0, 3).map(([key, param]) => (
            <div key={key} className="flex flex-col items-center">
              <div className={`w-6 h-6 rounded-full border-2 ${pedal.enabled ? 'bg-white/20 border-white/40' : 'bg-zinc-700 border-zinc-600'}`}>
                <div 
                  className="w-0.5 h-2 bg-white/80 mx-auto mt-0.5 origin-bottom"
                  style={{ transform: `rotate(${((pedal.params[key] || param.default) / 100) * 270 - 135}deg)` }}
                />
              </div>
              <span className={`text-[8px] mt-1 ${pedal.enabled ? 'text-white/70' : 'text-zinc-500'}`}>
                {param.label}
              </span>
            </div>
          ))}
        </div>
        
        {/* Footswitch */}
        <button
          onClick={() => onToggle(pedal.id)}
          className={`w-full py-2 rounded-lg border-2 transition-all ${
            pedal.enabled 
              ? 'bg-black/30 border-white/30 hover:bg-black/40' 
              : 'bg-zinc-700/50 border-zinc-600 hover:bg-zinc-700'
          }`}
        >
          <Power className={`w-5 h-5 mx-auto ${pedal.enabled ? 'text-white' : 'text-zinc-500'}`} />
        </button>
        
        {/* Expand for detailed controls */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full mt-2 text-xs flex items-center justify-center gap-1 ${pedal.enabled ? 'text-white/70' : 'text-zinc-500'}`}
        >
          <Settings className="w-3 h-3" />
          <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {/* Expanded Controls */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className={`px-4 pb-4 space-y-3 border-t ${pedal.enabled ? 'border-white/20' : 'border-zinc-700'}`}>
              <div className="pt-3">
                {Object.entries(pedalType.params).map(([key, param]) => (
                  <div key={key} className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className={pedal.enabled ? 'text-white/70' : 'text-zinc-500'}>{param.label}</span>
                      <span className={pedal.enabled ? 'text-white' : 'text-zinc-400'}>{pedal.params[key] || param.default}</span>
                    </div>
                    <Slider
                      value={[pedal.params[key] || param.default]}
                      min={param.min}
                      max={param.max}
                      onValueChange={([v]) => onParamChange(pedal.id, key, v)}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Pedal Picker Modal
const PedalPicker = ({ isOpen, onClose, onAdd, existingTypes }) => {
  const categories = {
    drive: { name: 'Drive', color: 'orange' },
    filter: { name: 'Filter', color: 'emerald' },
    modulation: { name: 'Modulation', color: 'cyan' },
    dynamics: { name: 'Dynamics', color: 'slate' },
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4">Add Pedal</h3>
        
        {Object.entries(categories).map(([catKey, category]) => (
          <div key={catKey} className="mb-4">
            <h4 className={`text-sm font-medium text-${category.color}-400 mb-2`}>{category.name}</h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PEDAL_TYPES)
                .filter(([_, pedal]) => pedal.category === catKey)
                .map(([key, pedal]) => {
                  const Icon = pedal.icon;
                  const alreadyAdded = existingTypes.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => !alreadyAdded && onAdd(key)}
                      disabled={alreadyAdded}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        alreadyAdded
                          ? 'bg-zinc-800/30 border-zinc-700/50 opacity-50 cursor-not-allowed'
                          : `bg-gradient-to-br ${pedal.color} border-white/20 hover:scale-105`
                      }`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-white">{pedal.name}</div>
                        <div className="text-xs text-white/60">{pedal.description}</div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
        
        <Button variant="ghost" onClick={onClose} className="w-full mt-4">
          Cancel
        </Button>
      </motion.div>
    </motion.div>
  );
};

export const GuitarPedalBoard = ({
  trackId,
  pedals = [],
  onChange,
}) => {
  const [localPedals, setLocalPedals] = useState(pedals);
  const [showPicker, setShowPicker] = useState(false);
  
  // Sync with parent
  useEffect(() => {
    setLocalPedals(pedals);
  }, [pedals]);
  
  // Add new pedal
  const handleAddPedal = useCallback((type) => {
    const pedalType = PEDAL_TYPES[type];
    const newPedal = {
      id: `pedal_${Date.now()}`,
      type,
      enabled: true,
      params: Object.fromEntries(
        Object.entries(pedalType.params).map(([key, param]) => [key, param.default])
      ),
    };
    const updated = [...localPedals, newPedal];
    setLocalPedals(updated);
    onChange?.(updated);
    setShowPicker(false);
  }, [localPedals, onChange]);
  
  // Toggle pedal
  const handleToggle = useCallback((pedalId) => {
    const updated = localPedals.map(p => 
      p.id === pedalId ? { ...p, enabled: !p.enabled } : p
    );
    setLocalPedals(updated);
    onChange?.(updated);
  }, [localPedals, onChange]);
  
  // Update param
  const handleParamChange = useCallback((pedalId, param, value) => {
    const updated = localPedals.map(p => 
      p.id === pedalId ? { ...p, params: { ...p.params, [param]: value } } : p
    );
    setLocalPedals(updated);
    onChange?.(updated);
  }, [localPedals, onChange]);
  
  // Remove pedal
  const handleRemove = useCallback((pedalId) => {
    const updated = localPedals.filter(p => p.id !== pedalId);
    setLocalPedals(updated);
    onChange?.(updated);
  }, [localPedals, onChange]);
  
  // Reorder pedals
  const handleReorder = useCallback((newOrder) => {
    setLocalPedals(newOrder);
    onChange?.(newOrder);
  }, [onChange]);
  
  return (
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-400" />
          <h3 className="font-semibold text-white">Pedal Board</h3>
          <span className="text-xs text-zinc-500">({localPedals.length} pedals)</span>
        </div>
        <Button
          size="sm"
          onClick={() => setShowPicker(true)}
          className="bg-violet-600 hover:bg-violet-500"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Pedal
        </Button>
      </div>
      
      {/* Signal Flow */}
      <div className="flex items-center gap-1 mb-4 text-xs text-zinc-500">
        <span className="px-2 py-1 bg-zinc-800 rounded">Input</span>
        <span>→</span>
        {localPedals.map((p, i) => (
          <span key={p.id} className="flex items-center gap-1">
            <span className={`px-2 py-1 rounded ${p.enabled ? 'bg-gradient-to-r ' + PEDAL_TYPES[p.type]?.color + ' text-white' : 'bg-zinc-800 text-zinc-500'}`}>
              {PEDAL_TYPES[p.type]?.name}
            </span>
            {i < localPedals.length - 1 && <span>→</span>}
          </span>
        ))}
        {localPedals.length > 0 && <span>→</span>}
        <span className="px-2 py-1 bg-zinc-800 rounded">Amp</span>
      </div>
      
      {/* Pedals */}
      {localPedals.length === 0 ? (
        <div className="text-center py-8">
          <Zap className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No pedals added</p>
          <p className="text-zinc-600 text-xs">Click "Add Pedal" to build your chain</p>
        </div>
      ) : (
        <Reorder.Group
          axis="x"
          values={localPedals}
          onReorder={handleReorder}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        >
          {localPedals.map(pedal => (
            <Reorder.Item key={pedal.id} value={pedal}>
              <Pedal
                pedal={pedal}
                onToggle={handleToggle}
                onParamChange={handleParamChange}
                onRemove={handleRemove}
              />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}
      
      {/* Pedal Picker Modal */}
      <AnimatePresence>
        {showPicker && (
          <PedalPicker
            isOpen={showPicker}
            onClose={() => setShowPicker(false)}
            onAdd={handleAddPedal}
            existingTypes={localPedals.map(p => p.type)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuitarPedalBoard;
